import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react'
import { MdClose, MdAdd, MdPrint, MdSearch, MdReceipt, MdDelete } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux'
import Header from '../components/header';
import AppLayout from '../components/AppLayout';
import { getData, postData } from '../utils/serverHelper';
import { POS_BILLING, POS_BILLING_ID, SERVICES, PATIENTS, PRODUCTS, BUSINESS_PRESET } from '../utils/urls';
import { toast } from 'react-toastify';
import { 
  Button, 
  Box, 
  TextField, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  Divider, 
  Grid, 
  Card, 
  CardContent, 
  Autocomplete,
  Tabs,
  Tab
} from '@mui/material';

const Billing = () => {
    const dispatch = useDispatch(); 
    const selectorAuth = useSelector(state=>state.auth); 
    
    // POS Billing States
    const [posServices, setPosServices] = useState([]);
    const [posProducts, setPosProducts] = useState([]);
    const [posPatients, setPosPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [cart, setCart] = useState([]);
    const [posDiscount, setPosDiscount] = useState(0);
    const [posDiscountType, setPosDiscountType] = useState('fixed');
    const [additionalCharge, setAdditionalCharge] = useState(0);
    const [additionalChargeType, setAdditionalChargeType] = useState('fixed');
    const [taxRate, setTaxRate] = useState(18);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [notes, setNotes] = useState('');
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
    const [additionalChargeDialogOpen, setAdditionalChargeDialogOpen] = useState(false);
    const [posBills, setPosBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [posSearchText, setPosSearchText] = useState('');
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'services', 'products'
    const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
      full_name: '',
      contact: '',
      email: ''
    });
    const [addingCustomer, setAddingCustomer] = useState(false);
    const [customerAlias, setCustomerAlias] = useState('Customer'); // Default alias
    const [staffAlias, setStaffAlias] = useState('Staff'); // Default alias

// POS Billing Functions
useEffect(() => {
  if (selectorAuth?.user?.business_id) {
    fetchBusinessPreset();
    fetchPosServices();
    fetchPosProducts();
    fetchPosPatients();
    fetchPosBills();
  }
}, [selectorAuth?.user?.business_id]);

const fetchBusinessPreset = async () => {
  try {
    const response = await getData(BUSINESS_PRESET);
    if (response?.success && response?.data) {
      setCustomerAlias(response.data.patient_alias || 'Customer');
      setStaffAlias(response.data.staff_alias || 'Staff');
    } else if (response?.patient_alias) {
      setCustomerAlias(response.patient_alias || 'Customer');
      setStaffAlias(response.staff_alias || 'Staff');
    }
  } catch (error) {
    console.error('Error fetching business preset:', error);
    // Keep defaults
  }
};

const fetchPosServices = async () => {
  try {
    const data = await getData(SERVICES);
    // Handle different response structures
    if (Array.isArray(data)) {
      setPosServices(data);
    } else if (data?.data && Array.isArray(data.data)) {
      setPosServices(data.data);
    } else if (data?.services && Array.isArray(data.services)) {
      setPosServices(data.services);
    } else {
      setPosServices([]);
    }
  } catch (error) {
    console.error('Error fetching services:', error);
    setPosServices([]);
  }
};

const fetchPosProducts = async () => {
  try {
    const response = await getData(PRODUCTS);
    if (response?.success) {
      setPosProducts(response.data || []);
    } else if (Array.isArray(response)) {
      setPosProducts(response);
    } else {
      setPosProducts([]);
    }
  } catch (error) {
    console.error('Error fetching products:', error);
    setPosProducts([]);
  }
};

const fetchPosPatients = async () => {
  try {
    const data = await getData(PATIENTS);
    // Handle different response structures
    if (Array.isArray(data)) {
      setPosPatients(data);
    } else if (data?.data && Array.isArray(data.data)) {
      setPosPatients(data.data);
    } else if (data?.patients && Array.isArray(data.patients)) {
      setPosPatients(data.patients);
    } else {
      setPosPatients([]);
    }
  } catch (error) {
    console.error('Error fetching patients:', error);
    setPosPatients([]);
  }
};

const handleAddCustomer = async () => {
  if (!newCustomer.full_name || !newCustomer.contact) {
    toast.error(`Please enter ${customerAlias.toLowerCase()} name and phone number`);
    return;
  }

  setAddingCustomer(true);
  try {
    const response = await postData(PATIENTS, {
      full_name: newCustomer.full_name,
      contact: newCustomer.contact,
      email: newCustomer.email || null
    });

    if (response?.success || Array.isArray(response) || response?.data) {
      const createdPatient = Array.isArray(response) ? response[0] : 
                            response?.data ? (Array.isArray(response.data) ? response.data[0] : response.data) : 
                            response;
      
      toast.success(`${customerAlias} added successfully`);
      
      // Refresh patient list
      await fetchPosPatients();
      
      // Select the newly created customer
      setSelectedPatient(createdPatient);
      
      // Close dialog and reset form
      setAddCustomerDialogOpen(false);
      setNewCustomer({ full_name: '', contact: '', email: '' });
    } else {
      toast.error(`Failed to add ${customerAlias.toLowerCase()}`);
    }
  } catch (error) {
    console.error(`Error adding ${customerAlias.toLowerCase()}:`, error);
    toast.error(error?.response?.data?.error?.message || `Failed to add ${customerAlias.toLowerCase()}`);
  } finally {
    setAddingCustomer(false);
  }
};

const fetchPosBills = async () => {
  try {
    const response = await getData(POS_BILLING);
    if (response?.success) {
      setPosBills(response.data || []);
    } else if (Array.isArray(response)) {
      setPosBills(response);
    } else {
      setPosBills([]);
    }
  } catch (error) {
    console.error('Error fetching POS bills:', error);
    setPosBills([]);
  }
};

const addToCart = (item) => {
  // Handle both services and products
  const itemType = item.service_id ? 'service' : 'product';
  
  // Check if the exact same item (same ID) already exists in cart
  const existingItemIndex = cart.findIndex(cartItem => {
    if (item.service_id && cartItem.service_id) {
      return cartItem.service_id === item.service_id;
    }
    if (item.product_id && cartItem.product_id) {
      return cartItem.product_id === item.product_id;
    }
    return false;
  });
  
  if (existingItemIndex !== -1) {
    // Item exists, increment quantity
    const updatedCart = [...cart];
    updatedCart[existingItemIndex] = {
      ...updatedCart[existingItemIndex],
      quantity: updatedCart[existingItemIndex].quantity + 1
    };
    setCart(updatedCart);
    toast.success(`${item.service || item.product_name} quantity increased`);
  } else {
    // New item, add to cart
    const cartItem = {
      item_name: item.service || item.product_name,
      item_type: itemType,
      unit_price: parseFloat(item.charges || item.selling_price || 0),
      quantity: 1,
      discount: 0,
      tax_rate: taxRate
    };
    
    if (item.service_id) {
      cartItem.service_id = item.service_id;
    }
    if (item.product_id) {
      cartItem.product_id = item.product_id;
      cartItem.mrp = parseFloat(item.mrp || 0);
      cartItem.product_code = item.product_code;
    }
    
    setCart([...cart, cartItem]);
    toast.success(`${item.service || item.product_name} added to cart`);
  }
};

const removeFromCart = (index) => {
  setCart(cart.filter((_, i) => i !== index));
};

const updateCartItem = (index, field, value) => {
  const updatedCart = [...cart];
  updatedCart[index] = {
    ...updatedCart[index],
    [field]: field === 'quantity' || field === 'unit_price' || field === 'discount'
      ? parseFloat(value) || 0
      : value
  };
  setCart(updatedCart);
};

const posCalculations = useMemo(() => {
  const subtotal = cart.reduce((sum, item) => {
    const itemTotal = (item.unit_price * item.quantity) - (item.discount || 0);
    return sum + itemTotal;
  }, 0);

  let discountAmount = 0;
  if (posDiscount > 0) {
    if (posDiscountType === 'percent') {
      discountAmount = (subtotal * posDiscount) / 100;
    } else {
      discountAmount = posDiscount;
    }
  }

  const afterDiscount = subtotal - discountAmount;

  let additionalChargeAmount = 0;
  if (additionalCharge > 0) {
    if (additionalChargeType === 'percent') {
      additionalChargeAmount = (afterDiscount * additionalCharge) / 100;
    } else {
      additionalChargeAmount = additionalCharge;
    }
  }

  const afterCharges = afterDiscount + additionalChargeAmount;
  const cgst = (afterCharges * taxRate) / 200;
  const sgst = (afterCharges * taxRate) / 200;
  const totalAmount = afterCharges + cgst + sgst;

  return {
    subtotal,
    discountAmount,
    afterDiscount,
    additionalChargeAmount,
    afterCharges,
    cgst,
    sgst,
    totalAmount
  };
}, [cart, posDiscount, posDiscountType, additionalCharge, additionalChargeType, taxRate]);

const handleCreatePosBill = async () => {
  if (cart.length === 0) {
    toast.error('Please add items to cart');
    return;
  }

  try {
    const billData = {
      business_id: selectorAuth?.user?.business_id,
      patient_id: selectedPatient?.patient_id || null,
      items: cart,
      discount: posDiscount,
      discount_type: posDiscountType,
      additional_charge: additionalCharge,
      additional_charge_type: additionalChargeType,
      tax_rate: taxRate,
      payment_method: paymentMethod,
      payment_status: 'paid',
      notes: notes
    };

    const response = await postData(POS_BILLING, billData);
    
    if (response?.success) {
      toast.success('Bill created successfully');
      setCart([]);
      setSelectedPatient(null);
      setPosDiscount(0);
      setAdditionalCharge(0);
      setNotes('');
      fetchPosBills();
      setSelectedBill(response.data);
      setPrintDialogOpen(true);
    } else {
      toast.error(response?.error || 'Failed to create bill');
    }
  } catch (error) {
    console.error('Error creating bill:', error);
    toast.error('Failed to create bill');
  }
};

const filteredPosBills = useMemo(() => {
  if (!posSearchText) return posBills;
  const search = posSearchText.toLowerCase();
  return posBills.filter(bill =>
    bill.invoice_number?.toLowerCase().includes(search) ||
    bill.patient_name?.toLowerCase().includes(search) ||
    bill.total_amount?.toString().includes(search)
  );
}, [posBills, posSearchText]);

// Filter and combine products and services for display
const availableItems = useMemo(() => {
  const search = posSearchText.toLowerCase();
  let items = [];
  
  // Add services
  const filteredServices = posServices.filter(service => {
    if (!search) return true;
    return service.service?.toLowerCase().includes(search) ||
           service.service_id?.toString().includes(search);
  }).map(service => ({ ...service, type: 'service' }));
  
  // Add products
  const filteredProducts = posProducts.filter(product => {
    if (!search) return true;
    return product.product_name?.toLowerCase().includes(search) ||
           product.product_code?.toLowerCase().includes(search) ||
           product.barcode?.toLowerCase().includes(search);
  }).map(product => ({ ...product, type: 'product' }));
  
  if (activeTab === 'services') {
    items = filteredServices;
  } else if (activeTab === 'products') {
    items = filteredProducts;
  } else {
    items = [...filteredServices, ...filteredProducts];
  }
  
  return items;
}, [posServices, posProducts, posSearchText, activeTab]);

const handlePrint = async () => {
  if (selectedBill && !selectedBill.items) {
    try {
      const response = await getData(POS_BILLING_ID(selectedBill.bill_id));
      if (response?.success) {
        setSelectedBill(response.data);
      }
    } catch (error) {
      console.error('Error fetching bill details:', error);
    }
  }
  
  setTimeout(() => {
    window.print();
  }, 100);
};

  return (
    <AppLayout>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          background: '#f7f7f7',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 2, md: 3 }
        }}
      >
        <Box sx={{ 
          maxWidth: '1500px', 
          mx: 'auto', 
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1f2937' }}>POS Billing</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="Settings" size="small" sx={{ backgroundColor: '#f3f4f6', fontWeight: 600 }} />
            </Box>
          </Box>
          
          <Grid container spacing={2.5} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Grid item xs={12} lg={8.5} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', backgroundColor: '#fff', mb: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {/* Action buttons removed as they're not functional yet */}
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by Item Name/Item Code or Scan Barcode"
                  value={posSearchText}
                  onChange={(e) => setPosSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <MdSearch style={{ marginRight: 8, color: '#6b7280' }} />
                  }}
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: '#f9fafb'
                    }
                  }}
                />

                {/* Products and Services Selection */}
                <Box sx={{ mb: 2 }}>
                  <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={`All (${posServices.length + posProducts.length})`} value="all" />
                    <Tab label={`Services (${posServices.length})`} value="services" />
                    <Tab label={`Products (${posProducts.length})`} value="products" />
                  </Tabs>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxHeight: 200,
                    overflowY: 'auto',
                    p: 1,
                    backgroundColor: '#f9fafb',
                    borderRadius: 1
                  }}>
                    {availableItems.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 2 }}>
                        No items found. {activeTab === 'all' ? 'Add services or products to get started.' : `Add ${activeTab} to get started.`}
                      </Typography>
                    ) : (
                      availableItems.map((item, index) => (
                        <Chip
                          key={item.service_id || item.product_id || index}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.service || item.product_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                ₹{parseFloat(item.charges || item.selling_price || 0).toFixed(2)}
                                {item.type === 'product' && item.stock_quantity !== undefined && (
                                  <span> • Stock: {item.stock_quantity}</span>
                                )}
                              </Typography>
                            </Box>
                          }
                          onClick={() => addToCart(item)}
                          icon={<MdAdd />}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            '&:hover': {
                              backgroundColor: '#5c6ac4',
                              color: 'white',
                              borderColor: '#5c6ac4',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(92, 106, 196, 0.3)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            height: 'auto',
                            py: 1
                          }}
                        />
                      ))
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* Cart Table */}
              <Paper elevation={0} sx={{ 
                p: 2, 
                borderRadius: 2, 
                border: '1px solid #e5e7eb', 
                backgroundColor: '#fff',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}>
                <TableContainer sx={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 1,
                  flex: 1,
                  overflow: 'auto'
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 60, fontWeight: 700 }}>NO</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>ITEMS</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>ITEM CODE</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>MRP</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>SP</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>QUANTITY</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>AMOUNT</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cart.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            Your cart is empty. Add services to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        cart.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {item.item_name}
                              {item.item_type === 'product' && (
                                <Chip label="Product" size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                              )}
                            </TableCell>
                            <TableCell>{item.service_id || item.product_code || '-'}</TableCell>
                            <TableCell align="right">₹{(item.mrp || item.unit_price).toFixed(2)}</TableCell>
                            <TableCell align="right">₹{item.unit_price.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => updateCartItem(index, 'quantity', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 1 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              ₹{((item.unit_price * item.quantity) - (item.discount || 0)).toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="error" onClick={() => removeFromCart(index)}>
                                <MdDelete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, px: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Items: {cart.length}
                  </Typography>
                  <Button size="small" color="error" onClick={() => setCart([])}>Clear All Items</Button>
                </Box>
              </Paper>
            </Grid>

            {/* Right Side - Bill History */}
            <Grid item xs={12} lg={3.5} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Paper elevation={0} sx={{ 
                p: 2, 
                borderRadius: 2, 
                border: '1px solid #e5e7eb', 
                backgroundColor: '#fff', 
                position: 'sticky', 
                top: 16,
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setDiscountDialogOpen(true)}
                    sx={{ 
                      borderColor: posDiscount > 0 ? '#5c6ac4' : '#e5e7eb', 
                      color: posDiscount > 0 ? '#5c6ac4' : '#374151', 
                      flex: 1, 
                      textTransform: 'none',
                      fontWeight: posDiscount > 0 ? 600 : 400
                    }}
                  >
                    {posDiscount > 0 ? `Discount: ${posDiscountType === 'percent' ? `${posDiscount}%` : `₹${posDiscount.toFixed(2)}`}` : 'Add Discount'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setAdditionalChargeDialogOpen(true)}
                    sx={{ 
                      borderColor: additionalCharge > 0 ? '#5c6ac4' : '#e5e7eb', 
                      color: additionalCharge > 0 ? '#5c6ac4' : '#374151', 
                      flex: 1, 
                      textTransform: 'none',
                      fontWeight: additionalCharge > 0 ? 600 : 400
                    }}
                  >
                    {additionalCharge > 0 ? `Charge: ${additionalChargeType === 'percent' ? `${additionalCharge}%` : `₹${additionalCharge.toFixed(2)}`}` : 'Add Additional Charge'}
                  </Button>
                </Box>

                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, mb: 1.5 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>Bill details</Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Sub Total</Typography>
                      <Typography variant="body2">₹{posCalculations.subtotal.toFixed(2)}</Typography>
                    </Box>
                    {posCalculations.discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="success.main">Discount</Typography>
                        <Typography variant="body2" color="success.main">-₹{posCalculations.discountAmount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {posCalculations.additionalChargeAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Additional Charge</Typography>
                        <Typography variant="body2">+₹{posCalculations.additionalChargeAmount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Tax (CGST + SGST)</Typography>
                      <Typography variant="body2">₹{(posCalculations.cgst + posCalculations.sgst).toFixed(2)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ backgroundColor: '#e8f5e9', px: 2, py: 1.25, borderTop: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                      Total Amount   ₹{posCalculations.totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, mb: 1.5 }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>Received Amount</Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={posCalculations.totalAmount.toFixed(2)}
                      InputProps={{ readOnly: true }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="card">Card</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                        <MenuItem value="online">Online</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>{customerAlias} Details</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MdAdd />}
                      onClick={() => setAddCustomerDialogOpen(true)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
                    >
                      Add {customerAlias}
                    </Button>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Autocomplete
                      options={posPatients}
                      getOptionLabel={(option) => option.full_name ? `${option.full_name} (${option.contact || 'No phone'})` : 'Cash Sale'}
                      value={selectedPatient}
                      onChange={(event, newValue) => setSelectedPatient(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`Select or search ${customerAlias.toLowerCase()}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.patient_id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.contact} {option.email ? `• ${option.email}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      isOptionEqualToValue={(option, value) => option.patient_id === value?.patient_id}
                      noOptionsText={`No ${customerAlias.toLowerCase()}s found`}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MdPrint />}
                    onClick={handleCreatePosBill}
                    disabled={cart.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    Save & Print
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCreatePosBill}
                    disabled={cart.length === 0}
                    sx={{ backgroundColor: '#5c6ac4', '&:hover': { backgroundColor: '#4c56a8' }, textTransform: 'none' }}
                  >
                    Save Bill
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Print Dialog */}
          <Dialog
            open={printDialogOpen}
            onClose={() => setPrintDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            <DialogTitle sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>Invoice Preview</Typography>
                <Box>
                  <IconButton 
                    onClick={handlePrint} 
                    sx={{ 
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                    }}
                  >
                    <MdPrint />
                  </IconButton>
                  <IconButton 
                    onClick={() => setPrintDialogOpen(false)}
                    sx={{ 
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                    }}
                  >
                    <MdClose />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedBill && (
                <Box id="invoice-content" sx={{ p: 3 }}>
                  {/* Invoice Header */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedBill.business_name || 'Hospital'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBill.business_address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact: {selectedBill.business_contact} | Email: {selectedBill.business_email}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Invoice Details */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Invoice Number:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedBill.invoice_number}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Date:</Typography>
                      <Typography variant="body1">
                        {moment(selectedBill.bill_date).format('DD MMM YYYY')}
                      </Typography>
                    </Grid>
                    {selectedBill.patient_name && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">{customerAlias}:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBill.patient_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Contact:</Typography>
                          <Typography variant="body1">{selectedBill.patient_contact}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Items Table */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBill.items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.item_name}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">₹{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                            <TableCell align="right">₹{parseFloat(item.total_price).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Totals */}
                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Typography variant="body2">
                      Subtotal: ₹{parseFloat(selectedBill.subtotal).toFixed(2)}
                    </Typography>
                    {selectedBill.discount > 0 && (
                      <Typography variant="body2" color="success.main">
                        Discount: -₹{parseFloat(selectedBill.discount).toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      CGST ({selectedBill.tax_rate / 2}%): ₹{parseFloat(selectedBill.cgst).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      SGST ({selectedBill.tax_rate / 2}%): ₹{parseFloat(selectedBill.sgst).toFixed(2)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total: ₹{parseFloat(selectedBill.total_amount).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Payment Method: {selectedBill.payment_method?.toUpperCase()}
                    </Typography>
                  </Box>

                  {selectedBill.notes && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Notes: {selectedBill.notes}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Thank you for your business!
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrintDialogOpen(false)}>Close</Button>
              <Button onClick={handlePrint} variant="contained" startIcon={<MdPrint />}>
                Print
              </Button>
            </DialogActions>
          </Dialog>

          {/* Discount Dialog */}
          <Dialog
            open={discountDialogOpen}
            onClose={() => setDiscountDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Add Discount</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={posDiscountType}
                    label="Discount Type"
                    onChange={(e) => setPosDiscountType(e.target.value)}
                  >
                    <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                    <MenuItem value="percent">Percentage (%)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={posDiscountType === 'percent' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'}
                  fullWidth
                  type="number"
                  value={posDiscount}
                  onChange={(e) => setPosDiscount(parseFloat(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 0, step: posDiscountType === 'percent' ? 0.1 : 1 }}
                  helperText={
                    posDiscount > 0 && posDiscountType === 'percent'
                      ? `Discount: ₹${((posCalculations.subtotal * posDiscount) / 100).toFixed(2)}`
                      : posDiscount > 0
                      ? `Discount: ₹${posDiscount.toFixed(2)}`
                      : ''
                  }
                />
                {posDiscount > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setPosDiscount(0);
                      setDiscountDialogOpen(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Remove Discount
                  </Button>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDiscountDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setDiscountDialogOpen(false)}
                sx={{ backgroundColor: '#5c6ac4' }}
              >
                Apply Discount
              </Button>
            </DialogActions>
          </Dialog>

          {/* Additional Charge Dialog */}
          <Dialog
            open={additionalChargeDialogOpen}
            onClose={() => setAdditionalChargeDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Add Additional Charge</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Charge Type</InputLabel>
                  <Select
                    value={additionalChargeType}
                    label="Charge Type"
                    onChange={(e) => setAdditionalChargeType(e.target.value)}
                  >
                    <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                    <MenuItem value="percent">Percentage (%)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={additionalChargeType === 'percent' ? 'Charge Percentage (%)' : 'Charge Amount (₹)'}
                  fullWidth
                  type="number"
                  value={additionalCharge}
                  onChange={(e) => setAdditionalCharge(parseFloat(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 0, step: additionalChargeType === 'percent' ? 0.1 : 1 }}
                  helperText={
                    additionalCharge > 0 && additionalChargeType === 'percent'
                      ? `Charge: ₹${((posCalculations.afterDiscount * additionalCharge) / 100).toFixed(2)}`
                      : additionalCharge > 0
                      ? `Charge: ₹${additionalCharge.toFixed(2)}`
                      : ''
                  }
                />
                {additionalCharge > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setAdditionalCharge(0);
                      setAdditionalChargeDialogOpen(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Remove Charge
                  </Button>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setAdditionalChargeDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setAdditionalChargeDialogOpen(false)}
                sx={{ backgroundColor: '#5c6ac4' }}
              >
                Apply Charge
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Customer Dialog */}
          <Dialog
            open={addCustomerDialogOpen}
            onClose={() => {
              setAddCustomerDialogOpen(false);
              setNewCustomer({ full_name: '', contact: '', email: '' });
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Add New {customerAlias}</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                  label={`${customerAlias} Name *`}
                  fullWidth
                  value={newCustomer.full_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, full_name: e.target.value })}
                  required
                  size="small"
                />
                <TextField
                  label="Phone Number *"
                  fullWidth
                  value={newCustomer.contact}
                  onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                  required
                  size="small"
                  inputProps={{ maxLength: 15 }}
                />
                <TextField
                  label="Email (Optional)"
                  fullWidth
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  size="small"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setAddCustomerDialogOpen(false);
                  setNewCustomer({ full_name: '', contact: '', email: '' });
                }}
                disabled={addingCustomer}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddCustomer}
                disabled={addingCustomer || !newCustomer.full_name || !newCustomer.contact}
                sx={{ backgroundColor: '#5c6ac4' }}
              >
                {addingCustomer ? 'Adding...' : `Add ${customerAlias}`}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </AppLayout>
  )
}

export default Billing;



{/* <div className="table-wrapper">
<div className='table-container'>
  <div className="td-head lg:w-full justify-between">
    {
      selectorAuth?.user?.role==='supper_admin'??
      <p className="td-element">Id</p>
    }
   {
    selectorAuth?.user?.role==='supper_admin'??
    <p className="td-element">Business Id</p>
   }
    <p className="td-element">Business Name</p>
    <p className="td-element">Plan</p>
    <p className="td-element">Month</p>
    <p className="td-element">Amount</p>
    <p className="td-element">Discount</p>
    <p className="td-element">Final Amount</p>
    <p className="td-element">Transection Id</p>
    <p className="td-element">Purchase Date</p>
    <p className="td-element">Actions</p>
  </div>
  <div className="flex-1">
    {
        selector?.billings?.map(
            (e,i)=><div className='td-row lg:w-full justify-betweens' key={i}>
                {
                  selectorAuth?.user?.role==='supper_admin'??
                  <p className='td-element'>{e?.id}</p>
                }
                {
                  selectorAuth?.user?.role==='supper_admin'??
                  <p className='td-element'>{e?.business_id}</p>
                }
                <p className='td-element'>{e?.business_name}</p>
                <p className='td-element'>{e?.plan_name}</p>
                <p className='td-element'>{e?.month_count}</p>
                <p className='td-element'>₹{e?.month_price}</p>
                <p className='td-element'>₹{((e?.month_price || 0) - (e?.final_amount || 0)).toFixed(2)}</p>
                <p className='td-element'>₹{parseFloat(e?.final_amount).toFixed(1)}</p>
                <p className='td-element'>{e?.txn_id}</p>
                <p className='td-element'>{moment(e?.purchase_date).format('DD-MM-yyyy')}</p>
                <p className='td-element highlited'
                onClick={()=>getStatus(
                  e?.id,
                  e?.txn_id
                )}
                >See status</p>
            </div>
        )
    }
  </div>
  </div>
</div> */}